import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { List, Typography } from 'antd';
import styled from 'styled-components';

import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';
import type { Event } from '../../../../services/bb-api/event';

export type EventOverviewProps = {
	dataSource: (Event & { eventId: number })[];
	loading: boolean;
};

export const EventOverviewMobile = (props: EventOverviewProps) => {
	const navigateTimeout = useRef<NodeJS.Timeout | null>(null);

	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		if (navigateTimeout.current !== null) clearTimeout(navigateTimeout.current);

		const itemId = location.hash.replace('#', '');
		const itemSelector = `li#item-${itemId}`;
		const listItemElement = document.querySelector(itemSelector);

		if (listItemElement === null) return;

		navigateTimeout.current = setTimeout(() => {
			listItemElement.scrollIntoView({ behavior: 'auto', block: 'center' });
		}, 100);
	}, [props.dataSource, location.hash]);

	const renderItem = (event: Event & { eventId: number }) => {
		const eventId = String(event.eventId);

		const handleEventNavigation = () => {
			history.replace({ hash: eventId, search: location.search });
			history.push(`/events/${eventId}`);
		};

		return (
			<List.Item id={eventId} onClick={handleEventNavigation}>
				<StyledListItemContainer>
					<Typography.Text type="secondary">{txt('global.title')}</Typography.Text>
					<Typography.Text>{event.title}</Typography.Text>

					<Typography.Text type="secondary">{txt('global.from')}</Typography.Text>
					<Typography.Text ellipsis>{formatDate(new Date(event?.dateStart) ?? null)}</Typography.Text>
					
					<Typography.Text type="secondary">{txt('global.to')}</Typography.Text>
					<Typography.Text ellipsis>{formatDate(new Date(event?.dateEnd) ?? null)}</Typography.Text>

					<Typography.Paragraph className="body-content" ellipsis={{ rows: 2 }}>
						{event.body}
					</Typography.Paragraph>
				</StyledListItemContainer>
			</List.Item>
		);
	};

	return (
		<List
			size="large"
			itemLayout="vertical"
			loading={props.loading}
			renderItem={renderItem}
			dataSource={props.dataSource}
		/>
	);
};

const StyledListItemContainer = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 0ch 1ch;

	.body-content {
		margin-top: 2ch;
		white-space: pre-wrap;
		grid-column: span 2;
	}
`;
